import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MicIcon from '@mui/icons-material/Mic';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { DateRange } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import ControlledDateRangePicker from 'shared/components/ControlledDateRangePicker';
import AdLibraryService from 'services/AdLibraryService';
import VideoCreativeService from 'services/VideoCreativeService';
import { ScrapedAd, Keyword, EnumValue } from './types';
import { shouldPoll, isPollingPaused } from 'shared/pollingGate';
import AdDetailDialog, { AD_DETAIL_TAB_KEYS, AdDetailTabKey } from './AdDetailDialog';
import BatchScriptRequestDialog from './BatchScriptRequestDialog';
import UploadAdDialog from './UploadAdDialog';
import useSortableTable from 'shared/useSortableTable';
import useColumnPreferences from './useColumnPreferences';
import ColumnPicker from './ColumnPicker';
import { ColumnCtx, computeAllFeatureDists, formatEnumLabel, pipelineRank } from './adColumns';

const service = new AdLibraryService();
const ciService = new VideoCreativeService();

// Creative-intelligence feature filters (server-side). Mirrors the dropdowns
// the standalone Video Creative tab used before the merge.
const FEATURE_FILTER_FIELDS = [
  { key: 'awarenessLevel', label: 'Awareness', enumType: 'awareness_level' },
  { key: 'hookDevice', label: 'Hook Device', enumType: 'hook_device' },
  { key: 'persuasionAngle', label: 'Persuasion Angle', enumType: 'persuasion_angle' },
  { key: 'proofType', label: 'Proof Type', enumType: 'proof_type' },
  { key: 'narrativeStructure', label: 'Narrative Structure', enumType: 'narrative_structure' },
  { key: 'primaryEmotion', label: 'Emotion', enumType: 'primary_emotion' },
  { key: 'presenter', label: 'Presenter', enumType: 'presenter' },
  { key: 'productionFormat', label: 'Production Format', enumType: 'production_format' },
  { key: 'tone', label: 'Tone', enumType: 'tone' },
  { key: 'offerType', label: 'Offer Type', enumType: 'offer_type' },
  { key: 'ctaType', label: 'CTA Type', enumType: 'cta_type' },
];

export default function AdBrowser() {
  const location = useLocation();
  const history = useHistory();
  const [ads, setAds] = useState<ScrapedAd[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [keywordFilter, setKeywordFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'' | 'manual' | 'scraped'>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDateRange, setStartDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const [fetchedRange, setFetchedRange] = useState<DateRange<Dayjs>>([null, null]);
  // Default the Fetched Ads view to ads updated within the last 2 weeks, so
  // stale ads drop off automatically. Users can widen/clear it.
  const [updatedRange, setUpdatedRange] = useState<DateRange<Dayjs>>([dayjs().subtract(2, 'week'), null]);
  const [featureFilters, setFeatureFilters] = useState<Record<string, string>>({});
  const [showFeatureFilters, setShowFeatureFilters] = useState(false);
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [enums, setEnums] = useState<Record<string, EnumValue[]>>({});
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [selected, setSelected] = useState<string[]>([]);

  const prefs = useColumnPreferences();

  // Sync viewAdId and tab with URL query params ?ad=<adId>&tab=<tabKey>
  const params = new URLSearchParams(location.search);
  const urlAdId = params.get('ad');
  const urlTab = params.get('tab') as AdDetailTabKey | null;
  const [viewAdId, setViewAdIdState] = useState<string | null>(urlAdId);
  const [viewTab, setViewTab] = useState<AdDetailTabKey | undefined>(
    urlTab && AD_DETAIL_TAB_KEYS.includes(urlTab) ? urlTab : undefined,
  );
  const suppressUrlSync = useRef(false);

  const replaceUrl = useCallback((adId: string | null, tab?: AdDetailTabKey | null) => {
    const nextParams = new URLSearchParams(location.search);
    if (adId) {
      nextParams.set('ad', adId);
      if (tab) nextParams.set('tab', tab);
      else nextParams.delete('tab');
    } else {
      nextParams.delete('ad');
      nextParams.delete('tab');
    }
    const search = nextParams.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
    suppressUrlSync.current = true;
  }, [history, location.pathname, location.search]);

  const setViewAdId = useCallback((adId: string | null) => {
    setViewAdIdState(adId);
    setViewTab(undefined);
    replaceUrl(adId, null);
  }, [replaceUrl]);

  const handleDetailTabChange = useCallback((tab: AdDetailTabKey) => {
    setViewTab(tab);
    if (viewAdId) replaceUrl(viewAdId, tab);
  }, [viewAdId, replaceUrl]);

  // Sync from URL changes (e.g., browser back/forward)
  useEffect(() => {
    if (suppressUrlSync.current) {
      suppressUrlSync.current = false;
      return;
    }
    const p = new URLSearchParams(location.search);
    const id = p.get('ad');
    const t = p.get('tab') as AdDetailTabKey | null;
    setViewAdIdState(id);
    setViewTab(t && AD_DETAIL_TAB_KEYS.includes(t) ? t : undefined);
  }, [location.search]);

  const [pipelineConfirmAdId, setPipelineConfirmAdId] = useState<string | null>(null);

  // Batch "Create script requests" dialog state + post-success snackbar.
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchSnackbar, setBatchSnackbar] = useState<{ count: number; generated: boolean; adsNeedingPipeline: number } | null>(null);

  // Manual upload (replaces the standalone Transcription tab).
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadSnackbar, setUploadSnackbar] = useState<{ currentStep?: string } | null>(null);
  const [processingAdIds, setProcessingAdIds] = useState<Set<string>>(new Set());

  // Per-row "transcribe button is mid-click" and batch button busy flag.
  // Used to render a spinner directly on the trigger button while the API
  // call is in flight. processingAdIds (above) drives the optimistic
  // pipeline-status chip and the polling effect that swaps it out once the
  // backend reports real progress.
  const [transcribingAdIds, setTranscribingAdIds] = useState<Set<string>>(new Set());
  const [batchAnalyzing, setBatchAnalyzing] = useState(false);

  const pipelineComparators = useMemo(() => ({
    pipeline: (a: ScrapedAd, b: ScrapedAd) => pipelineRank(a) - pipelineRank(b),
  }), []);
  const { sorted: sortedAds, sortField, sortDir, handleSort } =
    useSortableTable(ads, 'impressionRank', 'asc', pipelineComparators);

  // Client-side text search filter
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return sortedAds;
    const q = searchQuery.toLowerCase();
    return sortedAds.filter(ad =>
      ad.adId.toLowerCase().includes(q) ||
      (ad.pageName || '').toLowerCase().includes(q) ||
      (ad.headline || '').toLowerCase().includes(q)
    );
  }, [sortedAds, searchQuery]);

  // Per-loaded-rows distribution for every feature column — drives the
  // prevalence coloring and the breakdown tooltip shown on cell hover.
  const featureDist = useMemo(() => computeAllFeatureDists(ads), [ads]);

  // Template-ad usage map: ad_id → number of generated scripts that use
  // this ad as a template. Fetched once on mount (it's a single small
  // aggregate query). Drives the "Scripts" column and the click-to-Scripts
  // navigation. Refreshed silently after the user kicks off a new script
  // batch (BatchScriptRequestDialog onSuccess).
  const [templateUsage, setTemplateUsage] = useState<Map<string, number>>(new Map());
  const refreshTemplateUsage = useCallback(async () => {
    const result = await ciService.getTemplateAdUsage();
    if (!result?.usage) return;
    const next = new Map<string, number>();
    for (const u of result.usage) next.set(u.adId, u.usageCount);
    setTemplateUsage(next);
  }, []);
  useEffect(() => { refreshTemplateUsage(); }, [refreshTemplateUsage]);

  const handleScriptsClick = useCallback((adId: string) => {
    history.push(`/ad-library/scripts?templateAdId=${encodeURIComponent(adId)}`);
  }, [history]);

  const columnCtx = useMemo<ColumnCtx>(
    () => ({ featureDist, processingAdIds, templateUsage, onScriptsClick: handleScriptsClick }),
    [featureDist, processingAdIds, templateUsage, handleScriptsClick],
  );

  const handleAdUpdated = useCallback((updated: ScrapedAd) => {
    setAds(prev => prev.map(a => a.adId === updated.adId ? { ...a, ...updated } : a));
    const allDone = updated.transcriptionStatus === 'DONE' && updated.extractionStatus === 'DONE';
    const anyFailed = updated.transcriptionStatus === 'FAILED' || updated.extractionStatus === 'FAILED';
    if (allDone || anyFailed) {
      setProcessingAdIds(prev => { const next = new Set(prev); next.delete(updated.adId); return next; });
    }
  }, []);

  // Poll the rows we've marked as processing (e.g. after a batch "Save &
  // generate" kicked off pipelines). For each, re-fetch and drop it from
  // the set once both transcription + extraction reach a terminal state.
  useEffect(() => {
    if (processingAdIds.size === 0) return;
    let cancelled = false;

    const poll = async () => {
      // Gate: skip while the tab is hidden or the user has been idle >5 min, so a
      // dashboard left open doesn't keep hitting Snowflake. Resumes on activity.
      if (!shouldPoll()) return;
      const ids = Array.from(processingAdIds);
      const results = await Promise.all(ids.map(id => service.getAd(id)));
      if (cancelled) return;
      const terminalIds: string[] = [];
      setAds(prev => {
        const byId = new Map(results.filter(Boolean).map(r => [r!.adId, r!]));
        return prev.map(a => {
          const fresh = byId.get(a.adId);
          if (!fresh) return a;
          const tDone = fresh.transcriptionStatus === 'DONE';
          const eDone = fresh.extractionStatus === 'DONE';
          const anyFailed = fresh.transcriptionStatus === 'FAILED' || fresh.extractionStatus === 'FAILED';
          if ((tDone && eDone) || anyFailed) terminalIds.push(a.adId);
          return { ...a, ...fresh };
        });
      });
      if (terminalIds.length > 0) {
        setProcessingAdIds(prev => {
          const next = new Set(prev);
          for (const id of terminalIds) next.delete(id);
          return next;
        });
      }
    };

    const intervalId = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [processingAdIds]);

  // Surface when the polling gate has paused live updates (hidden tab /
  // idle user) while rows are still processing, so they don't look stuck.
  // Checked on a cheap local timer — no network involved.
  const [pollingPaused, setPollingPaused] = useState(false);
  useEffect(() => {
    if (processingAdIds.size === 0) {
      setPollingPaused(false);
      return;
    }
    const id = setInterval(() => setPollingPaused(isPollingPaused()), 2000);
    return () => clearInterval(id);
  }, [processingAdIds]);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    const reqParams: any = { limit: rowsPerPage, offset: page * rowsPerPage };
    if (keywordFilter) reqParams.keywordId = keywordFilter;
    if (statusFilter) reqParams.transcriptionStatus = statusFilter;
    if (sourceFilter) reqParams.sourcePlatform = sourceFilter;
    if (startDateRange[0]) reqParams.startDateFrom = startDateRange[0].format('YYYY-MM-DD');
    if (startDateRange[1]) reqParams.startDateTo = startDateRange[1].format('YYYY-MM-DD');
    if (fetchedRange[0]) reqParams.fetchedFrom = fetchedRange[0].format('YYYY-MM-DD');
    if (fetchedRange[1]) reqParams.fetchedTo = fetchedRange[1].format('YYYY-MM-DD');
    if (updatedRange[0]) reqParams.updatedFrom = updatedRange[0].format('YYYY-MM-DD');
    if (updatedRange[1]) reqParams.updatedTo = updatedRange[1].format('YYYY-MM-DD');
    for (const [k, v] of Object.entries(featureFilters)) {
      if (v) reqParams[k] = v;
    }
    if (tagFilter.length) reqParams.tag = tagFilter;
    const data = await service.listAds(reqParams);
    if (data) {
      setAds(data.ads);
      setTotal(data.total);
    }
    setLoading(false);
  }, [page, rowsPerPage, keywordFilter, statusFilter, sourceFilter, startDateRange, fetchedRange, updatedRange, featureFilters, tagFilter]);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  useEffect(() => {
    service.listKeywords().then(data => { if (data) setKeywords(data.keywords); });
    service.listAdTags().then(res => { if (res) setTagOptions(res.tags); });
    ciService.listEnums().then(result => {
      if (result?.enums) {
        const grouped: Record<string, EnumValue[]> = {};
        for (const e of result.enums) {
          if (!grouped[e.enumType]) grouped[e.enumType] = [];
          grouped[e.enumType].push(e);
        }
        setEnums(grouped);
      }
    });
  }, []);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(e.target.checked ? ads.map(a => a.adId) : []);
  };

  const handleSelect = (adId: string) => {
    setSelected(prev => prev.includes(adId) ? prev.filter(id => id !== adId) : [...prev, adId]);
  };

  // Bulk "Analyze Selected" runs the full transcribe → analyze → extract
  // pipeline on each selected ad. No dedicated batch endpoint — we just
  // loop service.runPipeline(adId), which is idempotent server-side (ads
  // already DONE no-op out at each stage). Polling effect picks up the
  // backend status as each stage advances and removes ids from
  // processingAdIds once both transcription + extraction reach terminal.
  const handleBatchAnalyze = async () => {
    if (!selected.length) return;
    const ids = [...selected];
    setBatchAnalyzing(true);
    setProcessingAdIds(prev => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    setTranscribingAdIds(prev => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    try {
      // Fire all kickoffs in parallel — each one returns quickly (just an
      // enqueue), so wall-clock stays close to a single call regardless
      // of the selection size.
      await Promise.all(ids.map(id => service.runPipeline(id)));
      setSelected([]);
    } finally {
      setBatchAnalyzing(false);
      setTranscribingAdIds(prev => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      fetchAds();
    }
  };

  const handleTranscribe = async (adId: string) => {
    setProcessingAdIds(prev => new Set(prev).add(adId));
    setTranscribingAdIds(prev => new Set(prev).add(adId));
    try {
      await service.transcribeAd(adId);
    } finally {
      setTranscribingAdIds(prev => { const next = new Set(prev); next.delete(adId); return next; });
      fetchAds();
    }
  };

  const handleRunPipeline = async () => {
    if (!pipelineConfirmAdId) return;
    const adId = pipelineConfirmAdId;
    setPipelineConfirmAdId(null);
    setProcessingAdIds(prev => new Set(prev).add(adId));
    const result = await service.runPipeline(adId);
    if (result === null) {
      setProcessingAdIds(prev => { const next = new Set(prev); next.delete(adId); return next; });
    }
    fetchAds();
  };

  const handleFeatureFilterChange = (key: string, value: string) => {
    setFeatureFilters(prev => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setKeywordFilter('');
    setStatusFilter('');
    setSourceFilter('');
    setSearchQuery('');
    setStartDateRange([null, null]);
    setFetchedRange([null, null]);
    setUpdatedRange([null, null]);
    setFeatureFilters({});
    setTagFilter([]);
    setPage(0);
  };

  const paginationProps = {
    component: 'div' as const,
    count: total,
    page,
    rowsPerPage,
    onPageChange: (_: unknown, p: number) => setPage(p),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); },
    rowsPerPageOptions: [10, 20, 50],
  };

  const visibleColumns = prefs.visibleColumns;
  // checkbox + chevron + data columns + actions
  const colCount = visibleColumns.length + 2;
  const activeFeatureFilterCount = Object.values(featureFilters).filter(Boolean).length;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search ID, page, headline..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 220, flex: '0 1 240px' }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Keyword</InputLabel>
            <Select value={keywordFilter} label="Keyword" onChange={(e) => { setKeywordFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {keywords.map(kw => <MenuItem key={kw.keywordId} value={kw.keywordId}>{kw.keyword}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Transcription</InputLabel>
            <Select value={statusFilter} label="Transcription" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="QUEUED">Queued</MenuItem>
              <MenuItem value="PROCESSING">Processing</MenuItem>
              <MenuItem value="DONE">Done</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source"
              onChange={(e) => { setSourceFilter(e.target.value as '' | 'manual' | 'scraped'); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="manual">Manual uploads</MenuItem>
              <MenuItem value="scraped">Scraped only</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            size="small"
            options={tagOptions}
            value={tagFilter}
            onChange={(_, next) => { setTagFilter(next); setPage(0); }}
            sx={{ minWidth: 200, flex: '0 1 240px' }}
            renderInput={params => <TextField {...params} label="Tags" placeholder="Filter by tag" />}
          />
          <ControlledDateRangePicker dateRange={startDateRange} onDateRangeChange={(r) => { setStartDateRange(r); setPage(0); }} label="Started Running" width={230} />
          <ControlledDateRangePicker dateRange={fetchedRange} onDateRangeChange={(r) => { setFetchedRange(r); setPage(0); }} label="Fetched" width={230} />
          <ControlledDateRangePicker dateRange={updatedRange} onDateRangeChange={(r) => { setUpdatedRange(r); setPage(0); }} label="Last Updated" width={230} />
          <Button size="small" onClick={() => setShowFeatureFilters(v => !v)}>
            {showFeatureFilters ? 'Hide' : 'Show'} creative filters
            {activeFeatureFilterCount > 0 ? ` (${activeFeatureFilterCount})` : ''}
          </Button>
          <Button size="small" onClick={handleClearFilters}>Clear</Button>
          <Tooltip title="Refresh ad list (cached across tab switches)" arrow>
            <span>
              <IconButton size="small" disabled={loading}
                onClick={() => { fetchAds(); refreshTemplateUsage(); }}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {selected.length > 0 && (
            <Button variant="outlined" size="small" onClick={handleBatchAnalyze} disabled={batchAnalyzing}
              startIcon={batchAnalyzing ? <CircularProgress size={14} /> : undefined}>
              {batchAnalyzing
                ? `Analyzing ${selected.length}…`
                : `Analyze Selected (${selected.length})`}
            </Button>
          )}
          {selected.length > 0 && (
            <Button variant="outlined" size="small" onClick={() => setBatchDialogOpen(true)}>
              Create script requests ({selected.length})
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={() => setUploadDialogOpen(true)}>
            Upload ad
          </Button>
          <ColumnPicker prefs={prefs} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{total} ads</Typography>
          {pollingPaused && processingAdIds.size > 0 && (
            <Chip size="small" variant="outlined" color="warning"
              label="Live updates paused — move the mouse to resume"
              sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
        </Box>

        {/* Collapsible creative-intelligence feature filters */}
        <Collapse in={showFeatureFilters}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {FEATURE_FILTER_FIELDS.map(ff => (
              <FormControl key={ff.key} size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{ff.label}</InputLabel>
                <Select
                  value={featureFilters[ff.key] || ''}
                  label={ff.label}
                  onChange={(e) => handleFeatureFilterChange(ff.key, e.target.value as string)}
                >
                  <MenuItem value="">All</MenuItem>
                  {(enums[ff.enumType] || []).map(ev => (
                    <MenuItem key={ev.enumValue} value={ev.enumValue}>
                      {ev.displayName || formatEnumLabel(ev.enumValue)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        </Collapse>
      </Paper>

      {/* Top pagination */}
      <TablePagination {...paginationProps} />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < ads.length}
                  checked={ads.length > 0 && selected.length === ads.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Actions</TableCell>
              {visibleColumns.map(col => (
                <TableCell key={col.id} align={col.align}>
                  <Tooltip title={col.headerTooltip || ''} arrow>
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortField === (col.sortKey || col.id)}
                        direction={sortField === (col.sortKey || col.id) ? sortDir : 'asc'}
                        onClick={() => handleSort(col.sortKey || col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      <Box component="span">{col.label}</Box>
                    )}
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>No ads found</TableCell></TableRow>
            ) : (
              filtered.map((rawAd) => {
                // Optimistic display: a row that was JUST submitted for
                // transcription/pipeline should immediately show PROCESSING
                // in the pipeline-status chip, even though the backend may
                // not have written the QUEUED state yet. The polling effect
                // takes over once the backend reports actual progress.
                const isInFlight = processingAdIds.has(rawAd.adId);
                const ad = isInFlight
                  && rawAd.transcriptionStatus !== 'DONE'
                  && rawAd.transcriptionStatus !== 'FAILED'
                  ? { ...rawAd, transcriptionStatus: 'PROCESSING' as const }
                  : rawAd;
                const isTranscribing = transcribingAdIds.has(ad.adId);
              return (
                <TableRow key={ad.adId} hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setViewAdId(ad.adId)}>
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(ad.adId)} onChange={() => handleSelect(ad.adId)} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={!ad.videoUrl ? 'No video' : ad.transcriptionJobId ? 'Already transcribed' : isTranscribing ? 'Transcribing…' : 'Transcribe'} arrow>
                        <span>
                          <IconButton size="small" onClick={() => handleTranscribe(ad.adId)}
                            disabled={!ad.videoUrl || !!ad.transcriptionJobId || isTranscribing}>
                            {isTranscribing
                              ? <CircularProgress size={16} />
                              : <MicIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Run pipeline" arrow>
                        <IconButton size="small" onClick={() => setPipelineConfirmAdId(ad.adId)}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  {visibleColumns.map(col => (
                    <TableCell key={col.id} align={col.align}>
                      {col.render(ad, columnCtx)}
                    </TableCell>
                  ))}
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bottom pagination */}
      <TablePagination {...paginationProps} />

      {/* Pipeline confirmation dialog */}
      <Dialog open={!!pipelineConfirmAdId} onClose={() => setPipelineConfirmAdId(null)}>
        <DialogTitle>Confirm Pipeline</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(() => {
              const ad = ads.find(a => a.adId === pipelineConfirmAdId);
              const allDone = ad?.transcriptionStatus === 'DONE' && ad?.extractionStatus === 'DONE';
              return allDone
                ? 'This ad has already been fully processed. Are you sure you want to re-process it?'
                : 'Are you sure you want to process this ad through the full pipeline (transcribe, analyze, extract)?';
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPipelineConfirmAdId(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRunPipeline}>
            {(() => {
              const ad = ads.find(a => a.adId === pipelineConfirmAdId);
              const allDone = ad?.transcriptionStatus === 'DONE' && ad?.extractionStatus === 'DONE';
              return allDone ? 'Re-process' : 'Process';
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      <AdDetailDialog adId={viewAdId} initialTab={viewTab} onTabChange={handleDetailTabChange} onClose={() => setViewAdId(null)} onRefresh={fetchAds} onAdUpdated={handleAdUpdated} />

      <UploadAdDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={({ adId, currentStep }) => {
          setUploadDialogOpen(false);
          setUploadSnackbar({ currentStep });
          // Refresh the list so the new row appears with its pipeline status,
          // and open the ad-detail page so the user can watch the pipeline
          // progress in place.
          fetchAds();
          setViewAdId(adId);
        }}
      />
      <Snackbar
        open={!!uploadSnackbar}
        autoHideDuration={6000}
        onClose={() => setUploadSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={uploadSnackbar
          ? `Ad uploaded — pipeline started${uploadSnackbar.currentStep ? ` (${uploadSnackbar.currentStep})` : ''}. It will appear in the list shortly.`
          : ''}
      />

      <BatchScriptRequestDialog
        open={batchDialogOpen}
        selectedAds={ads.filter(a => selected.includes(a.adId))}
        onClose={() => setBatchDialogOpen(false)}
        onSuccess={({ adsNeedingPipelineIds, ...rest }) => {
          setBatchDialogOpen(false);
          setSelected([]);
          setBatchSnackbar(rest);
          if (adsNeedingPipelineIds.length > 0) {
            setProcessingAdIds(prev => {
              const next = new Set(prev);
              for (const id of adsNeedingPipelineIds) next.add(id);
              return next;
            });
          }
        }}
      />
      <Snackbar
        open={!!batchSnackbar}
        autoHideDuration={6000}
        onClose={() => setBatchSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={batchSnackbar
          ? `Created ${batchSnackbar.count} script request${batchSnackbar.count === 1 ? '' : 's'}` +
            (batchSnackbar.generated ? ' — generating now' : '') +
            (batchSnackbar.adsNeedingPipeline > 0
              ? `; ${batchSnackbar.adsNeedingPipeline} ad${batchSnackbar.adsNeedingPipeline === 1 ? '' : 's'} need pipeline first (automatic)`
              : '') +
            '. View in the Scripts tab.'
          : ''}
      />
    </Box>
  );
}
